import { store } from '@/app/store';
import { sentryService } from '@/services/telemetry/sentry';
import type { UploadPhoto, UploadPhotosResponse } from '@/shared/types/api';
import { api, instrument, requireSessionId, unwrap } from '../client';
import type { BinaryInput } from '../http';

export function uploadPhotosWithFaceRecognition(
  photos: UploadPhoto[],
  source: string | null = null
): Promise<UploadPhotosResponse> {
  const photoCount = photos?.length ?? 0;

  return instrument<UploadPhotosResponse>(
    {
      span: 'api.upload_photos_face_recognition',
      errorTag: 'sessions_upload_session_photos',
      tags: { 'api.photo_count': photoCount, 'api.source': source },
      before: {
        message: 'Uploading photos with face recognition',
        data: { photo_count: photoCount, source },
      },
      errorExtra: { photo_count: photoCount, source },
      trackErrorCode: 'api_error_photo_upload',
    },
    async () => {
      const sessionId = requireSessionId();
      const data: Record<string, unknown> = {
        bucket_name: store.kiosk?.portrait_s3_bucket_name,
      };
      if (source) data.source = source;

      const binaries: Record<string, BinaryInput> = {};
      for (const photo of photos) {
        binaries[photo.uuid] = { blob: photo.blob, name: `${photo.uuid}.jpg` };
      }

      const startTime = Date.now();
      // The schema types each uploaded photo as an untyped record.
      const result = (await unwrap(
        api.POST('/v1/sessions/{session_id}/photos', {
          params: { path: { session_id: sessionId } },
          multipart: { data, binaries },
        })
      )) as unknown as UploadPhotosResponse;

      // Upload throughput metrics — done inline so we have access to duration.
      const duration = Date.now() - startTime;
      const totalBytes = photos.reduce(
        (sum, p) => sum + (p.blob?.size ?? 0),
        0
      );
      const throughputBytesPerSec =
        duration > 0 ? totalBytes / (duration / 1000) : 0;

      sentryService.trackMetric('upload.total_bytes', totalBytes, 'byte');
      sentryService.trackMetric(
        'upload.throughput_bytes_per_second',
        throughputBytesPerSec,
        'none'
      );
      sentryService.trackMetric('upload.photo_count', photos.length, 'none');
      sentryService.trackMetric('upload.duration_ms', duration, 'millisecond');
      sentryService.trackMemoryUsage('after_photo_upload');
      // Backend returns an array of photo records, each with its own
      // ``faces`` array; aggregate-detected count = sum of face counts
      // across photos. The previous code read ``result.faces`` on the
      // top-level response (which doesn't exist on the wire) and
      // permanently logged ``undefined``.
      const facesDetected = Array.isArray(result)
        ? result.reduce((sum, photo) => sum + (photo.faces?.length ?? 0), 0)
        : 0;

      sentryService.addBreadcrumb(
        'api',
        'Photos uploaded successfully',
        {
          photo_count: photoCount,
          faces_detected: facesDetected,
          duration_ms: duration,
          total_bytes: totalBytes,
          throughput_kbps: Math.round(throughputBytesPerSec / 1024),
        },
        'info'
      );

      return result;
    }
  );
}

function fetchSessionPhotos(
  optionalSessionId: string | undefined,
  silent: boolean
): Promise<unknown[]> {
  const sessionId = optionalSessionId ?? store.sessionId;
  if (!sessionId) {
    if (!silent) console.warn('[api] Cannot fetch photos: no session ID');
    return Promise.resolve([]);
  }
  return unwrap(
    api.GET('/v1/sessions/{session_id}/photos', {
      params: { path: { session_id: sessionId } },
      ...(silent && { silent }),
    })
  ).catch((e: Error) => {
    console.warn('[api] Photo fetch failed:', e.message);
    return [];
  });
}

// Returns [] on missing session_id or any fetch error. Callers use this for
// background UI updates where failure should not escalate.
export function getUploadedPhotosInSession(
  optionalSessionId?: string
): Promise<unknown[]> {
  return fetchSessionPhotos(optionalSessionId, false);
}

// Silent version for polling — no Sentry breadcrumbs for successful requests.
export function getUploadedPhotosInSessionSilent(
  optionalSessionId?: string
): Promise<unknown[]> {
  return fetchSessionPhotos(optionalSessionId, true);
}
